import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Award,
  CalendarDays,
  Camera,
  ChevronRight,
  Clapperboard,
  Mail,
  MapPin,
  Phone,
  ShieldCheck,
  Sparkles,
  Star,
  Users,
} from "lucide-react";
import styled, { createGlobalStyle } from "styled-components";
import {
  getPublicProfile,
  MarketplaceProfile,
} from "../../../Redux/ApiCalls/Dashboard/MarketplaceAPI";
import { resolveImage } from "../../../Utils/helper";
import { MARKETPLACE_FONTS } from "./MarketplaceConstants";
import marketplaceHomeBg from "../../../assets/images/marketplace/marketplace_home_bg.webp";
import teamsImg from "../../../assets/images/marketplace/teams.webp";
import team2Img from "../../../assets/images/marketplace/team2.webp";
import team3Img from "../../../assets/images/marketplace/team3.webp";
import team4Img from "../../../assets/images/marketplace/team4.webp";

const Fonts = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@500;600;700;800&family=Plus+Jakarta+Sans:wght@500;600;700;800&display=swap');
`;

const Page = styled.main`
  min-height: 100vh;
  background: #0d1012;
  color: var(--mp-headingColor);
  font-family: "Inter", ${MARKETPLACE_FONTS.SANS};

  [data-marketplace-theme="light"] & {
    background: #f6f7f4;
  }
`;

const Hero = styled.section`
  position: relative;
  min-height: 390px;
  padding: 28px 0 0;
  overflow: hidden;
  background: #0d1012;

  [data-marketplace-theme="light"] & {
    background: #f6f7f4;
  }
`;

const HeroImage = styled.img`
  position: absolute;
  inset: 28px 0 auto;
  left: 50%;
  width: min(920px, calc(100% - 48px));
  height: 318px;
  transform: translateX(-50%);
  object-fit: cover;
  border-radius: 4px;
  opacity: 0.64;

  [data-marketplace-theme="light"] & {
    opacity: 0.5;
  }
`;

const HeroShade = styled.div`
  position: absolute;
  inset: 28px 0 auto;
  left: 50%;
  width: min(920px, calc(100% - 48px));
  height: 318px;
  transform: translateX(-50%);
  border-radius: 4px;
  background:
    linear-gradient(90deg, rgba(10,12,14,0.92), rgba(10,12,14,0.48) 58%, rgba(10,12,14,0.26)),
    linear-gradient(0deg, rgba(10,12,14,0.78), rgba(10,12,14,0.05) 48%);

  [data-marketplace-theme="light"] & {
    background:
      linear-gradient(90deg, rgba(247,248,252,0.94), rgba(247,248,252,0.46) 58%, rgba(247,248,252,0.8)),
      linear-gradient(0deg, var(--mp-bodyBackground), rgba(247,248,252,0) 46%);
  }
`;

const Container = styled.div`
  width: min(920px, calc(100% - 48px));
  margin: 0 auto;
  position: relative;
  z-index: 1;

  @media (max-width: 720px) {
    width: min(100% - 32px, 920px);
  }
`;

const BackButton = styled.button`
  border: 1px solid var(--mp-sidebarBorder);
  border-radius: 999px;
  background: color-mix(in srgb, var(--mp-cardBackground) 82%, transparent);
  color: var(--mp-headingColor);
  min-height: 30px;
  padding: 0 11px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font: 700 10px/14px "Inter", ${MARKETPLACE_FONTS.SANS};
  backdrop-filter: blur(12px);
`;

const HeroGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 164px;
  gap: 22px;
  align-items: end;
  margin-top: 90px;

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
    margin-top: 54px;
  }
`;

const Chips = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 10px;
`;

const Chip = styled.span`
  border-radius: 999px;
  background: rgba(255, 222, 161, 0.16);
  border: 1px solid rgba(255, 222, 161, 0.28);
  color: #ffdea1;
  padding: 4px 9px;
  font-size: 10px;
  line-height: 14px;
  font-weight: 800;
`;

const Title = styled.h1`
  margin: 0 0 8px;
  color: var(--mp-heroHeadingColor);
  font-family: "Plus Jakarta Sans", ${MARKETPLACE_FONTS.SANS};
  font-size: clamp(28px, 4vw, 38px);
  line-height: 1.05;
  font-weight: 800;
`;

const Bio = styled.p`
  max-width: 440px;
  margin: 0;
  color: var(--mp-heroSubColor);
  font-size: 11px;
  line-height: 17px;
`;

const MetaRow = styled.div`
  display: flex;
  gap: 18px;
  flex-wrap: wrap;
  margin-top: 16px;
`;

const MetaItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  color: var(--mp-headingColor);
  font-size: 11px;
  font-weight: 700;
`;

const ContactCard = styled.aside`
  border: 1px solid var(--mp-sidebarBorder);
  border-radius: 12px;
  background: color-mix(in srgb, var(--mp-cardBackground) 88%, transparent);
  padding: 14px;
  box-shadow: 0 22px 60px rgba(0,0,0,0.22);
  backdrop-filter: blur(18px);
`;

const ContactTitle = styled.h2`
  margin: 0 0 8px;
  color: var(--mp-headingColor);
  font-size: 12px;
  line-height: 16px;
`;

const ContactAction = styled.button<{ $primary?: boolean }>`
  width: 100%;
  min-height: 32px;
  border: 1px solid ${p => (p.$primary ? "transparent" : "var(--mp-sidebarBorder)")};
  border-radius: 999px;
  background: ${p => (p.$primary ? "var(--mp-heroBtnBg)" : "transparent")};
  color: ${p => (p.$primary ? "#24180c" : "var(--mp-headingColor)")};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  font: 800 10px/14px "Inter", ${MARKETPLACE_FONTS.SANS};
  margin-top: 8px;
`;

const Content = styled.section`
  padding: 22px 0 64px;
`;

const MainGrid = styled.div`
  display: grid;
  grid-template-columns: 230px minmax(0, 1fr);
  gap: 18px;
  align-items: start;

  @media (max-width: 940px) {
    grid-template-columns: 1fr;
  }
`;

const Panel = styled.article`
  border: 1px solid var(--mp-sidebarBorder);
  border-radius: 12px;
  background: var(--mp-cardBackground);
  padding: 18px;
`;

const PanelTitle = styled.h2`
  margin: 0 0 18px;
  color: var(--mp-headingColor);
  font-size: 15px;
  line-height: 20px;
`;

const Stats = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 18px;

  @media (max-width: 720px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const Stat = styled.div`
  border-radius: 10px;
  background: color-mix(in srgb, var(--mp-cardBackground) 86%, var(--mp-headingColor));
  padding: 12px;
`;

const StatValue = styled.strong`
  display: block;
  color: var(--mp-headingColor);
  font-size: 18px;
  line-height: 22px;
`;

const StatLabel = styled.span`
  color: var(--mp-subColor);
  font-size: 9px;
`;

const Roster = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;

  @media (max-width: 680px) {
    grid-template-columns: 1fr;
  }
`;

const RosterCard = styled.div`
  min-height: 116px;
  border-radius: 10px;
  overflow: hidden;
  position: relative;
  background: #15181b;
`;

const RosterImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const RosterText = styled.div`
  position: absolute;
  inset: auto 0 0;
  padding: 12px;
  background: linear-gradient(0deg, rgba(0,0,0,0.82), rgba(0,0,0,0));
  color: #fff;
  font-size: 10px;
`;

const ResourceList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ResourceRow = styled.div`
  min-height: 34px;
  border-radius: 999px;
  background: color-mix(in srgb, var(--mp-cardBackground) 88%, var(--mp-headingColor));
  color: var(--mp-headingColor);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 7px 9px 7px 12px;
  font-size: 10px;
  font-weight: 700;
`;

const SmallBadge = styled.span`
  border-radius: 999px;
  background: rgba(255, 222, 161, 0.14);
  color: #ffdea1;
  padding: 4px 8px;
  font-size: 9px;
`;

const FALLBACK_PROFILE: MarketplaceProfile = {
  id: 0,
  profile_type: "studio",
  display_name: "Lumen Collective",
  bio: "A full-service creative production agency specializing in high-impact commercial campaigns and cinematic storytelling.",
  city: "Los Angeles",
  state: "CA",
  country: "USA",
  team_size: 15,
  profile_avg_rating: 4.9,
  profile_total_reviews: 120,
  services_offered: ["Commercial Film", "Creative Direction", "Post Production"],
  work_categories: ["Feature Film", "Brand Campaigns"],
  is_verified: true,
  created_at: "",
  updated_at: "",
};

const roster = [
  { name: "Sarah Jenkins", role: "Production Designer", image: team4Img },
  { name: "Marcus Thorne", role: "Gaffer", image: team3Img },
  { name: "Elena Rostova", role: "Line Producer", image: team2Img },
];

const recentProjects = [
  "Neon Urban Pulse Campaign",
  "Midnight at the Oasis",
  "Golden Hour Product Launch",
];

const MarketplaceAgencyDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [profile, setProfile] = useState<MarketplaceProfile | null>(null);

  useEffect(() => {
    if (!id) return;
    getPublicProfile(Number(id))
      .then(res => setProfile(res.data))
      .catch(() => setProfile(null));
  }, [id]);

  const agency = profile || FALLBACK_PROFILE;
  const heroImage = agency.profile_picture ? resolveImage(agency.profile_picture) : marketplaceHomeBg;
  const services = useMemo(() => {
    const values = [...(agency.services_offered || []), ...(agency.work_categories || [])];
    return values.length ? values.slice(0, 5) : FALLBACK_PROFILE.services_offered || [];
  }, [agency]);
  const location = [agency.city, agency.state || agency.country].filter(Boolean).join(", ") || "Global";

  return (
    <Page>
      <Fonts />
      <Hero>
        <HeroImage src={heroImage || teamsImg} alt={agency.display_name} />
        <HeroShade />
        <Container>
          <BackButton onClick={() => navigate("/marketplace/agency-directory")}>
            <ArrowLeft size={15} />
            Back to directory
          </BackButton>
          <HeroGrid>
            <div>
              <Chips>
                <Chip>Agency Team</Chip>
                {agency.is_verified && <Chip>Verified</Chip>}
                {services.slice(0, 2).map(service => <Chip key={service}>{service}</Chip>)}
              </Chips>
              <Title>{agency.display_name}</Title>
              <Bio>{agency.bio || FALLBACK_PROFILE.bio}</Bio>
              <MetaRow>
                <MetaItem><MapPin size={16} /> {location}</MetaItem>
                <MetaItem><Star size={16} fill="#ffdea1" color="#ffdea1" /> {(agency.profile_avg_rating || 4.9).toFixed(1)} rating</MetaItem>
                <MetaItem><Users size={16} /> {agency.team_size || 15} members</MetaItem>
              </MetaRow>
            </div>
            <ContactCard>
              <ContactTitle>Contact Agency</ContactTitle>
              <ContactAction $primary onClick={() => navigate("/marketplace/project-quote")}>
                Request Quote <ChevronRight size={16} />
              </ContactAction>
              <ContactAction><Mail size={15} /> Message Team</ContactAction>
              <ContactAction><Phone size={15} /> Reveal Contact</ContactAction>
            </ContactCard>
          </HeroGrid>
        </Container>
      </Hero>

      <Content>
        <Container>
          <Stats>
            <Stat><StatValue>120+</StatValue><StatLabel>Projects</StatLabel></Stat>
            <Stat><StatValue>{(agency.profile_avg_rating || 4.9).toFixed(1)}</StatValue><StatLabel>Avg rating</StatLabel></Stat>
            <Stat><StatValue>{agency.team_size || 15}</StatValue><StatLabel>Core team</StatLabel></Stat>
            <Stat><StatValue>98%</StatValue><StatLabel>On time</StatLabel></Stat>
          </Stats>

          <MainGrid>
            <Panel>
              <PanelTitle>Core Roster</PanelTitle>
              <Roster>
                {roster.map(person => (
                  <RosterCard key={person.name}>
                    <RosterImg src={person.image} alt={person.name} />
                    <RosterText>
                      <strong>{person.name}</strong>
                      <div>{person.role}</div>
                    </RosterText>
                  </RosterCard>
                ))}
              </Roster>
            </Panel>

            <Panel>
              <PanelTitle>Top Equipment</PanelTitle>
              <ResourceList>
                {["ARRI Alexa Mini LF", "Cooke Anamorphic Set", "Aputure Lighting Grid"].map(item => (
                  <ResourceRow key={item}>
                    {item}
                    <SmallBadge>Available</SmallBadge>
                  </ResourceRow>
                ))}
              </ResourceList>
            </Panel>

            <Panel>
              <PanelTitle>Recent Projects</PanelTitle>
              <ResourceList>
                {recentProjects.map(project => (
                  <ResourceRow key={project}>
                    <span>{project}</span>
                    <ChevronRight size={15} />
                  </ResourceRow>
                ))}
              </ResourceList>
            </Panel>

            <Panel>
              <PanelTitle>Capabilities</PanelTitle>
              <ResourceList>
                {services.map((service, index) => (
                  <ResourceRow key={service}>
                    {index % 3 === 0 ? <Clapperboard size={15} /> : index % 3 === 1 ? <Camera size={15} /> : <Sparkles size={15} />}
                    <span style={{ flex: 1 }}>{service}</span>
                    <SmallBadge>{index + 1} day</SmallBadge>
                  </ResourceRow>
                ))}
              </ResourceList>
            </Panel>

            <Panel>
              <PanelTitle>Agency Notes</PanelTitle>
              <Bio>
                Built for fast-moving production briefs, this team combines creative development,
                crew sourcing, equipment planning, and post supervision for campaigns that need a
                cinematic finish without losing operational control.
              </Bio>
            </Panel>

            <Panel>
              <PanelTitle>Trust Signals</PanelTitle>
              <ResourceList>
                <ResourceRow><ShieldCheck size={15} /> Verified quality checks</ResourceRow>
                <ResourceRow><Award size={15} /> Premium creative roster</ResourceRow>
                <ResourceRow><CalendarDays size={15} /> Deadline-first workflow</ResourceRow>
              </ResourceList>
            </Panel>
          </MainGrid>
        </Container>
      </Content>
    </Page>
  );
};

export default MarketplaceAgencyDetailPage;
